import React, { useContext } from 'react';
import { ExperienceContext, useConfigService } from '@thd-nucleus/experience-context';
import { params, shape, string, useDataModel } from '@thd-nucleus/data-sources';
import PropTypes from 'prop-types';
import { CountdownTimer } from '@thd-olt-component-react/countdown-timer';
import LoadingSkeleton from './Common/LoadingSkeleton';
import { useConfig, getEventBadge, getEventDetails, getStyle } from './Common/utils';

const renderBanner = ({
  shouldShowTimer,
  style,
  usedBackgroundImage,
  usedEventLink,
  usedEventMessage,
  usedTimerEventMessage,
  usedEndDate,
  usedFallbackColor,
  usedTextColor
}) => {
  const styles = {};
  if (usedBackgroundImage) {
    styles.backgroundImage = `url(${usedBackgroundImage})`;
  }
  if (usedFallbackColor) {
    styles.backgroundColor = usedFallbackColor;
  }
  if (usedTextColor) {
    styles.color = usedTextColor;
  }

  return (
    <a
      aria-label="Event Countdown Timer"
      href={usedEventLink}
      className={shouldShowTimer ? style.bannerWithTimerWrapper : style.bannerWrapper}
      style={styles}
      data-testid="event-countdown-timer-link"
    >
      {shouldShowTimer ? (
        <CountdownTimer
          shouldUseMessagingString
          shouldIncludeSvg
          shouldEventTimer
          invertText
          customMessagingString={usedTimerEventMessage}
          endDate={usedEndDate}
          fontSize="small"
          textColor={usedTextColor}
        />
      ) : (
        <div className={style.bannerText}>
          {usedEventMessage}
        </div>
      )}
    </a>
  );
};

const ProductPageEventCountdownTimer = ({
  eventLink, eventMessage, endDate, backgroundImage, itemId
}) => {
  const config = useConfig(useConfigService);
  const { channel } = useContext(ExperienceContext);
  const isMobile = channel === 'mobile';
  const { data, loading, error } = useDataModel('product', {
    variables: { itemId }
  });

  const eventBadge = getEventBadge(data?.product?.badges);

  const {
    usedBackgroundImage,
    usedEventMessage,
    usedTimerEventMessage,
    usedEventLink,
    usedEndDate,
    usedFallbackColor,
    eventStartDate,
    timerStartDate,
    isTimerEnabled,
    currentDate,
    usedTextColor
  } = getEventDetails(config, { eventLink, eventMessage, endDate, backgroundImage });

  if (error || currentDate > usedEndDate) {
    return null;
  }

  const style = getStyle(isMobile, usedTextColor);
  const shouldShowBanner = currentDate >= eventStartDate && currentDate <= usedEndDate;
  const shouldShowTimer = currentDate >= timerStartDate && currentDate <= usedEndDate && isTimerEnabled;

  if (loading && (config.alwaysShow || eventBadge) && shouldShowBanner) {
    return <LoadingSkeleton />;
  }

  if ((!eventBadge && !config.alwaysShow) || !shouldShowBanner) {
    return null;
  }

  return (
    <div data-component="PIPEventCountdownTimer" className="sui-@container">
      {shouldShowBanner
        && renderBanner({
          shouldShowTimer,
          style,
          usedBackgroundImage,
          usedEventLink,
          usedEventMessage,
          usedTimerEventMessage,
          usedEndDate,
          usedFallbackColor,
          usedTextColor
        })}
    </div>
  );
};

ProductPageEventCountdownTimer.propTypes = {
  endDate: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})]),
  eventMessage: PropTypes.string,
  eventLink: PropTypes.string,
  backgroundImage: PropTypes.string,
  itemId: PropTypes.string.isRequired
};

ProductPageEventCountdownTimer.defaultProps = {
  backgroundImage: '',
  eventLink: 'https://www.homedepot.com/b/Spring-Black-Friday/N-5yc1vZ1z1cn1p?catStyle=ShowProducts',
  eventMessage: 'Spring Black Friday',
  endDate: '2024-12-05T03:00:00-04:00'
};

ProductPageEventCountdownTimer.dataModel = {
  product: params({ itemId: string().isRequired() }).shape({
    itemId: string(),
    dataSources: string(),
    badges: params({ storeId: string() }).arrayOf(
      shape({
        endDate: string(),
        label: string(),
        name: string()
      })
    )
  })
};

ProductPageEventCountdownTimer.displayName = 'ProductPageEventCountdownTimer';

export { ProductPageEventCountdownTimer };
